import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import PaginationButton from '../paginationButton'
import PaginationArrowsButtons from './components/ArrowsButtons'
import InputSearchPagination from './components/InputSearch'

type PaginationProps = {
  currentPage: number
  totalPages: number
  setPage: Dispatch<SetStateAction<number | undefined>>
}

export const Pagination = ({ currentPage, totalPages, setPage }: PaginationProps) => {
  const [searchPage, setSearchPage] = useState(false)

  const isFirstPage = currentPage === 1
  const isAfterFirstPage = currentPage > 1
  const isNotLastPage = currentPage < totalPages
  const isLastPage = currentPage === totalPages

  const pages = useMemo(() => {
    if (totalPages <= 5) {
      return (
        <>
          <PaginationArrowsButtons
            setPage={setPage}
            currentPage={currentPage}
            totalPages={totalPages}
          />
          {Array(totalPages)
            .fill(0)
            .map((_, idx) => {
              const pageNumber = idx + 1
              const isCurrentPage = currentPage === pageNumber

              const variant = isCurrentPage ? 'selected' : 'default'

              return (
                <PaginationButton
                  onClick={() => setPage(idx === 0 ? undefined : pageNumber)}
                  disabled={isCurrentPage}
                  variant={variant}
                  key={idx}
                  data-testid={`pagination_button_${pageNumber}`}
                >
                  {pageNumber}
                </PaginationButton>
              )
            }
          )}
          <PaginationArrowsButtons
            kind='next'
            setPage={setPage}
            currentPage={currentPage}
            totalPages={totalPages}
          />
        </>
      )
    }

    if (currentPage < totalPages / 2) {
      return (
        <>
          <PaginationArrowsButtons
            setPage={setPage}
            currentPage={currentPage}
            totalPages={totalPages}
          />
          {Array(3)
            .fill(0)
            .map((_, idx) => {
              const isFirstPageActive = isFirstPage && idx === 0
              const isMiddlePageActive = isAfterFirstPage && idx === 1

              const variant = (isFirstPageActive || isMiddlePageActive) && !searchPage
                ? 'selected'
                : 'default'

              const pageNumber = isFirstPage 
                ? currentPage + idx 
                : currentPage + (idx - 1)

              const targetPage = pageNumber > 1 ? pageNumber : undefined

              return (
                <PaginationButton
                  onClick={() => setPage(targetPage)}
                  variant={variant}
                  key={idx}
                  data-testid={`pagination_button_${idx + 1}`}
                >
                  {pageNumber}
                </PaginationButton>
              )
            }
          )}
          <InputSearchPagination
            setPage={setPage}
            totalPages={totalPages}
            setSearchPage={setSearchPage}
            searchPage={searchPage}
          />
          <PaginationButton onClick={() => setPage(totalPages)} data-testid={`pagination_button_${totalPages}`}>
            {totalPages}
          </PaginationButton>
          <PaginationArrowsButtons
            kind="next"
            setPage={setPage}
            currentPage={currentPage}
            totalPages={totalPages}
          />
        </>
      )
    }

    if (currentPage >= totalPages / 2) {
      return (
        <>
          <PaginationArrowsButtons
            setPage={setPage}
            currentPage={currentPage}
            totalPages={totalPages}
          />
          <PaginationButton onClick={() => setPage(undefined)} data-testid={`pagination_button_${1}`}>
            {1}
          </PaginationButton>
          <InputSearchPagination
            setPage={setPage}
            totalPages={totalPages}
            setSearchPage={setSearchPage}
            searchPage={searchPage}
          />
          {Array(2)
            .fill(0)
            .map((_, idx) => {              
              const pageBasedOnCurrent = currentPage + (idx - 1)
              const pageBasedOnTotal = totalPages + (idx - 2)

              const variant = idx === 1 && isNotLastPage && !searchPage 
                ? 'selected' 
                : 'default'

              const pageNumber = isNotLastPage 
                ? pageBasedOnCurrent 
                : pageBasedOnTotal

              return (
                <PaginationButton
                  onClick={() => setPage(pageNumber)}
                  variant={variant}
                  key={idx}
                >
                  {pageNumber}
                </PaginationButton>
              )
            }
          )}
          <PaginationButton 
            onClick={() => setPage(totalPages)} 
            data-testid={`pagination_button_${totalPages}`}
            variant={isLastPage && !searchPage ? 'selected' : 'default'}
          >
            {totalPages}
          </PaginationButton>
          <PaginationArrowsButtons
            kind="next"
            setPage={setPage}
            currentPage={currentPage}
            totalPages={totalPages}
          />
        </>
      )
    }
  }, [totalPages, currentPage, setPage, searchPage])

  return <div className="flex gap-2">{pages}</div>
}